import { useState, useRef, useEffect, useId } from "react";
import { cn } from "@/lib/utils";
import { scheduleCaptionMeasurement, cancelCaptionMeasurement } from "@/lib/captionMeasurement";

interface ReadMoreTextProps {
  text: string;
  prefix?: React.ReactNode;
  className?: string;
  paragraphClassName?: string;
  testId?: string;
  lineClamp?: number;
}

export function ReadMoreText({
  text,
  prefix,
  className,
  paragraphClassName,
  testId,
  lineClamp = 3,
}: ReadMoreTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);
  const reactId = useId();
  const bodyId = `read-more-body-${reactId}`;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const key = {};
    let disposed = false;
    const measure = () => {
      if (!disposed) scheduleCaptionMeasurement(key, {
        element: el, lineClamp, expanded, onMeasure: (overflowing) => {
          setIsOverflowing(overflowing);
          // Content/width/font changes can make an expanded caption fit again.
          // Do not leave it stuck expanded with an unnecessary Show less control.
          if (!overflowing) setExpanded(false);
        },
      });
    };
    measure();
    const ro = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
    ro?.observe(el);
    // Includes updates inside a stateful prefix, not only changes to its identity.
    const mutations = new MutationObserver(measure);
    mutations.observe(el, { childList: true, characterData: true, subtree: true });
    window.addEventListener("resize", measure);
    document.fonts?.addEventListener("loadingdone", measure);
    void document.fonts?.ready.then(measure);
    return () => {
      disposed = true;
      cancelCaptionMeasurement(key);
      ro?.disconnect();
      mutations.disconnect();
      window.removeEventListener("resize", measure);
      document.fonts?.removeEventListener("loadingdone", measure);
    };
  }, [text, prefix, lineClamp, expanded, paragraphClassName, className]);

  return (
    <div className={className}>
      <p
        ref={ref}
        id={bodyId}
        className={cn(
          "text-sm text-foreground leading-relaxed whitespace-pre-line",
          paragraphClassName
        )}
        style={!expanded ? {
          display: "-webkit-box",
          WebkitLineClamp: lineClamp,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        } : undefined}
        data-testid={testId}
      >
        {prefix}
        {text}
      </p>
      {/* Reserve the control's line before asynchronous measurement completes.
          Short captions keep an empty, non-interactive slot, not a fake button.
          This avoids moving the card/media below a long caption after paint. */}
      <div className="mt-0.5 h-5">
        {isOverflowing && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-controls={bodyId}
            className="block text-xs leading-5 font-medium text-primary hover:underline"
            data-testid="button-read-more"
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </div>
    </div>
  );
}

export default ReadMoreText;
